<template>
    <DefaultHeader />

    <div class="container-fluid digital-io-page">

        <div class="row justify-content-center">
            <div class="col-12 col-xl-11">

                <!-- En-tête -->
                <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">

                    <div>
                        <button type="button" class="btn btn-link text-decoration-none p-0 mb-2"
                            @click="$router.back()">
                            ← Retour
                        </button>

                        <h2 class="mb-1">
                            Entrées / sorties numériques
                        </h2>

                        <p class="text-muted mb-0">
                            Configuration des Digital I/O de
                            <strong v-if="balise">
                                {{ balise.name || balise.baliseId || `Balise ${baliseId}` }}
                            </strong>

                            <strong v-else>
                                Balise {{ baliseId }}
                            </strong>
                        </p>
                    </div>


                </div>


                <!-- Messages -->
                <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
                    {{ successMessage }}

                    <button type="button" class="btn-close" @click="successMessage = ''"></button>
                </div>

                <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
                    {{ errorMessage }}

                    <button type="button" class="btn-close" @click="errorMessage = ''"></button>
                </div>


                <div class="row g-4">

                    <!-- Zone principale -->
                    <div class="col-12 col-lg-8">

                        <!-- Création -->
                        <div v-if="showCreateForm" class="card create-card mb-4">
                            <div class="card-body">

                                <div class="d-flex justify-content-between align-items-start mb-4">

                                    <div>
                                        <h5 class="mb-1">
                                            Ajouter une entrée / sortie
                                        </h5>

                                        <p class="text-muted small mb-0">
                                            Définissez le type, l’index et les libellés associés à ses deux états.
                                        </p>
                                    </div>

                                    <button type="button" class="btn-close" :disabled="creating"
                                        @click="closeCreateForm"></button>

                                </div>


                                <form @submit.prevent="createDigitalIO">

                                    <div class="row g-3">

                                        <div class="col-12 col-md-6">

                                            <label for="digitalIOType" class="form-label">
                                                Type
                                            </label>

                                            <select id="digitalIOType" v-model="createForm.ioType" class="form-select"
                                                required>
                                                <option value="INPUT">
                                                    Entrée
                                                </option>

                                                <option value="OUTPUT">
                                                    Sortie
                                                </option>
                                            </select>

                                        </div>


                                        <div class="col-12 col-md-6">

                                            <label for="digitalIOIndex" class="form-label">
                                                Index
                                            </label>

                                            <input id="digitalIOIndex" v-model.number="createForm.index" type="number"
                                                min="0" class="form-control" required>

                                        </div>


                                        <div class="col-12 col-md-6">

                                            <label for="digitalIOOnName" class="form-label">
                                                Libellé état actif
                                            </label>

                                            <input id="digitalIOOnName" v-model="createForm.onName" type="text"
                                                maxlength="100" class="form-control" placeholder="Ex. Porte ouverte">

                                        </div>


                                        <div class="col-12 col-md-6">

                                            <label for="digitalIOOffName" class="form-label">
                                                Libellé état inactif
                                            </label>

                                            <input id="digitalIOOffName" v-model="createForm.offName" type="text"
                                                maxlength="100" class="form-control" placeholder="Ex. Porte fermée">

                                        </div>

                                    </div>


                                    <div class="d-flex justify-content-end gap-2 mt-4">

                                        <button type="button" class="btn btn-outline-secondary" :disabled="creating"
                                            @click="closeCreateForm">
                                            Annuler
                                        </button>

                                        <button type="submit" class="btn btn-primary"
                                            :disabled="creating || !canCreate">
                                            <span v-if="creating" class="spinner-border spinner-border-sm me-2"
                                                role="status"></span>

                                            {{
                                                creating
                                                    ? 'Ajout...'
                                                    : 'Ajouter'
                                            }}
                                        </button>

                                    </div>

                                </form>

                            </div>
                        </div>


                        <!-- Liste -->
                        <div class="card digital-io-card">

                            <div class="card-header bg-transparent">

                                <div class="d-flex justify-content-between align-items-center gap-3">

                                    <div>
                                        <h5 class="mb-1">
                                            Configuration
                                        </h5>

                                        <div class="text-muted small">
                                            {{ digitalIOs.length }}
                                            entrée{{ digitalIOs.length > 1 ? 's' : '' }} / sortie{{ digitalIOs.length >
                                            1 ? 's' : '' }}
                                        </div>
                                    </div>

                                    <button v-if="!showCreateForm" type="button"
                                        class="btn btn-primary d-flex align-items-center gap-2" @click="openCreateForm">
                                        <font-awesome-icon :icon="['fas', 'circle-plus']" />

                                        Ajouter
                                    </button>

                                </div>

                            </div>


                            <!-- Chargement -->
                            <div v-if="loading" class="text-center py-5">
                                <div class="spinner-border text-primary" role="status"></div>

                                <div class="text-muted mt-3">
                                    Chargement de la configuration...
                                </div>
                            </div>


                            <!-- Tableau -->
                            <div v-else-if="digitalIOs.length > 0" class="table-responsive">

                                <table class="table align-middle mb-0 digital-io-table">

                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Index</th>
                                            <th>État actif</th>
                                            <th>État inactif</th>
                                            <th>État actuel</th>
                                            <th class="text-end">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>


                                    <tbody>

                                        <tr v-for="io in sortedDigitalIOs" :key="io.id">

                                            <!-- Type -->
                                            <td>

                                                <template v-if="editingId === io.id">

                                                    <select v-model="editForm.ioType"
                                                        class="form-select form-select-sm">
                                                        <option value="INPUT">
                                                            Entrée
                                                        </option>

                                                        <option value="OUTPUT">
                                                            Sortie
                                                        </option>
                                                    </select>

                                                </template>

                                                <template v-else>

                                                    <span class="io-type" :class="io.ioType === 'INPUT'
                                                        ? 'io-type-input'
                                                        : 'io-type-output'">
                                                        {{
                                                            io.ioType === 'INPUT'
                                                                ? 'Entrée'
                                                                : 'Sortie'
                                                        }}
                                                    </span>

                                                </template>

                                            </td>


                                            <!-- Index -->
                                            <td>

                                                <template v-if="editingId === io.id">

                                                    <input v-model.number="editForm.index" type="number" min="0"
                                                        class="form-control form-control-sm io-index-input">

                                                </template>

                                                <template v-else>

                                                    <span class="io-index">
                                                        {{ io.index }}
                                                    </span>

                                                </template>

                                            </td>


                                            <!-- On name -->
                                            <td>

                                                <template v-if="editingId === io.id">

                                                    <input v-model="editForm.onName" type="text" maxlength="100"
                                                        class="form-control form-control-sm">

                                                </template>

                                                <template v-else>

                                                    <span v-if="io.onName">
                                                        {{ io.onName }}
                                                    </span>

                                                    <span v-else class="text-muted">
                                                        Non défini
                                                    </span>

                                                </template>

                                            </td>


                                            <!-- Off name -->
                                            <td>

                                                <template v-if="editingId === io.id">

                                                    <input v-model="editForm.offName" type="text" maxlength="100"
                                                        class="form-control form-control-sm">

                                                </template>

                                                <template v-else>

                                                    <span v-if="io.offName">
                                                        {{ io.offName }}
                                                    </span>

                                                    <span v-else class="text-muted">
                                                        Non défini
                                                    </span>

                                                </template>

                                            </td>


                                            <!-- Etat -->
                                            <td>

                                                <div class="d-flex flex-column align-items-start gap-1">

                                                    <span class="badge rounded-pill" :class="io.value
                                                        ? 'text-bg-success'
                                                        : 'text-bg-secondary'">
                                                        {{ getCurrentStateLabel(io) }}
                                                    </span>

                                                    <span v-if="io.lastUpdate" class="text-muted state-date">
                                                        {{
                                                            $filters.secondDateFormat(
                                                                io.lastUpdate
                                                            )
                                                        }}
                                                    </span>

                                                </div>

                                            </td>


                                            <!-- Actions -->
                                            <td class="text-end">

                                                <template v-if="editingId === io.id">

                                                    <div class="d-flex justify-content-end gap-2">

                                                        <button type="button" class="btn btn-sm btn-outline-secondary"
                                                            :disabled="saving" @click="cancelEdit">
                                                            Annuler
                                                        </button>

                                                        <button type="button" class="btn btn-sm btn-primary"
                                                            :disabled="saving" @click="saveEdit(io)">
                                                            <span v-if="saving"
                                                                class="spinner-border spinner-border-sm me-1"
                                                                role="status"></span>

                                                            Enregistrer
                                                        </button>

                                                    </div>

                                                </template>


                                                <template v-else>

                                                    <div class="d-flex justify-content-end gap-2">

                                                        <button type="button" class="btn btn-sm btn-outline-primary"
                                                            :disabled="editingId !== null" @click="startEdit(io)">
                                                            Modifier
                                                        </button>

                                                        <button type="button" class="btn btn-sm btn-outline-danger"
                                                            :disabled="editingId !== null || deletingId === io.id"
                                                            @click="deleteDigitalIO(io)">
                                                            <span v-if="deletingId === io.id"
                                                                class="spinner-border spinner-border-sm"
                                                                role="status"></span>

                                                            <template v-else>
                                                                Supprimer
                                                            </template>
                                                        </button>

                                                    </div>

                                                </template>

                                            </td>

                                        </tr>

                                    </tbody>

                                </table>

                            </div>


                            <!-- Vide -->
                            <div v-else class="empty-state">
                                <div class="empty-state-icon">
                                    I/O
                                </div>

                                <h5>
                                    Aucune entrée / sortie configurée
                                </h5>

                                <p class="text-muted mb-3">
                                    Cette balise ne possède actuellement aucune configuration Digital I/O.
                                </p>

                                <button type="button" class="btn btn-primary" @click="openCreateForm">
                                    <font-awesome-icon :icon="['fas', 'circle-plus']" class="me-1" />

                                    Ajouter une entrée / sortie
                                </button>
                            </div>

                        </div>

                    </div>


                    <!-- Copie depuis une autre balise -->
                    <div class="col-12 col-lg-4">
                        <div class="card copy-config-card">

                            <div class="card-header bg-transparent">
                                <h5 class="mb-1">
                                    Copier une configuration
                                </h5>

                                <div class="text-muted small">
                                    Sélectionnez une balise pour reprendre sa configuration Digital I/O.
                                </div>
                            </div>

                            <div class="card-body">

                                <label class="form-label">
                                    Balise source
                                </label>

                                <BaliseSelect class="w-100" :selectedBaliseId="sourceBaliseId"
                                    @baliseChange="onSourceBaliseChange" />

                                <div v-if="copyError" class="alert alert-danger py-2 mt-3 mb-0">
                                    {{ copyError }}
                                </div>

                                <div v-if="!sourceBaliseId" class="copy-empty-state">
                                    Sélectionnez une balise pour afficher ses entrées / sorties.
                                </div>

                                <div v-else-if="sourceLoading" class="text-center py-4">
                                    <div class="spinner-border spinner-border-sm text-primary" role="status"></div>

                                    <div class="text-muted small mt-2">
                                        Chargement de la configuration...
                                    </div>
                                </div>

                                <template v-else-if="sourceDigitalIOs.length > 0">

                                    <div class="source-io-list mt-3">
                                        <div v-for="io in sourceDigitalIOs" :key="io.id" class="source-io-item">

                                            <div class="d-flex align-items-center justify-content-between gap-2">
                                                <div class="d-flex align-items-center gap-2">
                                                    <span class="io-type" :class="io.ioType === 'INPUT'
                                                        ? 'io-type-input'
                                                        : 'io-type-output'">
                                                        {{ io.ioType === 'INPUT' ? 'Entrée' : 'Sortie' }}
                                                    </span>

                                                    <span class="io-index">
                                                        {{ io.index }}
                                                    </span>
                                                </div>
                                            </div>

                                            <div class="source-io-labels">
                                                <div>
                                                    <span class="text-muted">Actif :</span>
                                                    {{ io.onName || 'Non défini' }}
                                                </div>

                                                <div>
                                                    <span class="text-muted">Inactif :</span>
                                                    {{ io.offName || 'Non défini' }}
                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                    <button type="button" class="btn btn-primary w-100 mt-3"
                                        :disabled="copying" @click="copyDigitalIOs">
                                        <span v-if="copying" class="spinner-border spinner-border-sm me-2"
                                            role="status"></span>

                                        {{ copying ? 'Copie...' : 'Copier la configuration' }}
                                    </button>

                                    <div class="text-muted copy-help mt-2">
                                        Les entrées / sorties de même type et index seront mises à jour. Les autres seront ajoutées.
                                    </div>

                                </template>

                                <div v-else class="copy-empty-state">
                                    Cette balise ne possède aucune configuration Digital I/O à copier.
                                </div>

                            </div>

                        </div>
                    </div>

                </div>

            </div>
        </div>

    </div>

    <DefaultFooter />
</template>


<script>
import DefaultHeader from '@/components/DefaultHeader.vue'
import DefaultFooter from '@/components/DefaultFooter.vue'
import BaliseSelect from '@/components/display/BaliseSelect.vue'

import DigitalIOService from '@/services/digitalIO.service'
import BaliseService from '@/services/balise.service'


export default {

    components: {
        DefaultHeader,
        DefaultFooter,
        BaliseSelect
    },

    data() {
        return {
            balise: null,
            digitalIOs: [],

            loading: false,
            creating: false,
            saving: false,

            deletingId: null,
            editingId: null,

            showCreateForm: false,

            sourceBaliseId: null,
            sourceDigitalIOs: [],
            sourceLoading: false,
            copying: false,
            copyError: '',

            createForm: {
                ioType: 'INPUT',
                index: 0,
                onName: '',
                offName: ''
            },

            editForm: {
                ioType: 'INPUT',
                index: 0,
                onName: '',
                offName: ''
            },

            successMessage: '',
            errorMessage: ''
        };
    },

    computed: {

        baliseId() {
            return this.$route.params.baliseId;
        },

        sortedDigitalIOs() {
            return [...this.digitalIOs].sort((a, b) => {

                if (a.ioType !== b.ioType) {
                    return a.ioType === 'INPUT' ? -1 : 1;
                }

                return Number(a.index) - Number(b.index);
            });
        },

        canCreate() {
            return (
                this.createForm.ioType &&
                this.createForm.index !== '' &&
                this.createForm.index !== null &&
                Number(this.createForm.index) >= 0
            );
        }

    },

    mounted() {
        this.refreshData();
    },

    methods: {

        async refreshData() {

            this.loading = true;
            this.errorMessage = '';

            try {

                const [
                    digitalIOResponse,
                    baliseResponse
                ] = await Promise.all([
                    DigitalIOService.getBaliseDigitalIOs(
                        this.baliseId
                    ),
                    BaliseService.getBaliseInfo(
                        this.baliseId
                    )
                ]);

                this.digitalIOs =
                    digitalIOResponse.data || [];

                this.balise =
                    baliseResponse.data || null;

            } catch (e) {

                console.log(e);

                this.errorMessage =
                    e.response?.data?.message ||
                    "Impossible de récupérer la configuration des entrées / sorties.";

            } finally {

                this.loading = false;
            }
        },


        openCreateForm() {

            this.cancelEdit();

            this.resetCreateForm();

            this.successMessage = '';
            this.errorMessage = '';

            this.showCreateForm = true;
        },


        closeCreateForm() {

            if (this.creating) {
                return;
            }

            this.resetCreateForm();

            this.showCreateForm = false;
        },


        resetCreateForm() {

            this.createForm = {
                ioType: 'INPUT',
                index: 0,
                onName: '',
                offName: ''
            };
        },


        async createDigitalIO() {

            if (!this.canCreate) {
                return;
            }

            this.creating = true;
            this.successMessage = '';
            this.errorMessage = '';

            try {

                await DigitalIOService.createDigitalIO(
                    this.baliseId,
                    {
                        ioType: this.createForm.ioType,
                        index: Number(this.createForm.index),
                        onName: this.createForm.onName.trim() || null,
                        offName: this.createForm.offName.trim() || null
                    }
                );

                this.resetCreateForm();
                this.showCreateForm = false;

                this.successMessage =
                    "L'entrée / sortie a été ajoutée avec succès.";

                await this.refreshDigitalIOs();

            } catch (e) {

                console.log(e);

                this.errorMessage =
                    e.response?.data?.message ||
                    "Impossible d'ajouter l'entrée / sortie.";

            } finally {

                this.creating = false;
            }
        },


        startEdit(io) {

            this.showCreateForm = false;

            this.successMessage = '';
            this.errorMessage = '';

            this.editingId = io.id;

            this.editForm = {
                ioType: io.ioType,
                index: io.index,
                onName: io.onName || '',
                offName: io.offName || ''
            };
        },


        cancelEdit() {

            this.editingId = null;

            this.editForm = {
                ioType: 'INPUT',
                index: 0,
                onName: '',
                offName: ''
            };
        },


        async saveEdit(io) {

            if (
                this.editForm.index === '' ||
                this.editForm.index === null ||
                Number(this.editForm.index) < 0
            ) {
                return;
            }

            this.saving = true;
            this.successMessage = '';
            this.errorMessage = '';

            try {

                await DigitalIOService.updateDigitalIO(
                    io.id,
                    {
                        ioType: this.editForm.ioType,
                        index: Number(this.editForm.index),
                        onName: this.editForm.onName.trim() || null,
                        offName: this.editForm.offName.trim() || null
                    }
                );

                this.cancelEdit();

                this.successMessage =
                    "La configuration a été mise à jour.";

                await this.refreshDigitalIOs();

            } catch (e) {

                console.log(e);

                this.errorMessage =
                    e.response?.data?.message ||
                    "Impossible de modifier l'entrée / sortie.";

            } finally {

                this.saving = false;
            }
        },


        async deleteDigitalIO(io) {

            if (
                !confirm(
                    `Supprimer ${io.ioType === 'INPUT' ? "l'entrée" : "la sortie"} ${io.index} ?`
                )
            ) {
                return;
            }

            this.deletingId = io.id;
            this.successMessage = '';
            this.errorMessage = '';

            try {

                await DigitalIOService.deleteDigitalIO(
                    io.id
                );

                this.successMessage =
                    "L'entrée / sortie a été supprimée.";

                await this.refreshDigitalIOs();

            } catch (e) {

                console.log(e);

                this.errorMessage =
                    e.response?.data?.message ||
                    "Impossible de supprimer l'entrée / sortie.";

            } finally {

                this.deletingId = null;
            }
        },


        async refreshDigitalIOs() {

            const response =
                await DigitalIOService.getBaliseDigitalIOs(
                    this.baliseId
                );

            this.digitalIOs =
                response.data || [];
        },


        async onSourceBaliseChange(balise) {

            this.sourceBaliseId = balise?.id || null;
            this.sourceDigitalIOs = [];
            this.copyError = '';

            if (!this.sourceBaliseId) {
                return;
            }

            if (Number(this.sourceBaliseId) === Number(this.baliseId)) {
                this.sourceBaliseId = null;
                this.copyError = "Sélectionnez une autre balise que celle en cours d'édition.";
                return;
            }

            this.sourceLoading = true;

            try {

                const response =
                    await DigitalIOService.getBaliseDigitalIOs(
                        this.sourceBaliseId
                    );

                this.sourceDigitalIOs =
                    Array.isArray(response.data)
                        ? [...response.data].sort((a, b) => {

                            if (a.ioType !== b.ioType) {
                                return a.ioType === 'INPUT' ? -1 : 1;
                            }

                            return Number(a.index) - Number(b.index);
                        })
                        : [];

            } catch (e) {

                console.log(e);

                this.copyError =
                    e.response?.data?.message ||
                    "Impossible de récupérer la configuration de cette balise.";

            } finally {

                this.sourceLoading = false;
            }
        },


        async copyDigitalIOs() {

            if (
                !this.sourceBaliseId ||
                this.sourceDigitalIOs.length === 0 ||
                this.copying
            ) {
                return;
            }

            if (
                !confirm(
                    `Copier les ${this.sourceDigitalIOs.length} entrée(s) / sortie(s) de cette balise ?`
                )
            ) {
                return;
            }

            this.copying = true;
            this.copyError = '';
            this.successMessage = '';
            this.errorMessage = '';

            try {

                for (const sourceIO of this.sourceDigitalIOs) {

                    const data = {
                        ioType: sourceIO.ioType,
                        index: Number(sourceIO.index),
                        onName: sourceIO.onName || null,
                        offName: sourceIO.offName || null
                    };

                    const existingIO = this.digitalIOs.find(io =>
                        io.ioType === sourceIO.ioType &&
                        Number(io.index) === Number(sourceIO.index)
                    );

                    if (existingIO) {

                        await DigitalIOService.updateDigitalIO(
                            existingIO.id,
                            data
                        );

                    } else {

                        await DigitalIOService.createDigitalIO(
                            this.baliseId,
                            data
                        );
                    }
                }

                await this.refreshDigitalIOs();

                this.successMessage =
                    "La configuration Digital I/O a été copiée avec succès.";

            } catch (e) {

                console.log(e);

                this.copyError =
                    e.response?.data?.message ||
                    "Impossible de copier la configuration Digital I/O.";

            } finally {

                this.copying = false;
            }
        },


        getCurrentStateLabel(io) {

            if (io.value) {
                return io.onName || 'Actif';
            }

            return io.offName || 'Inactif';
        }

    }
}
</script>


<style scoped>
.digital-io-page {
    padding-top: 1.5rem;
    padding-bottom: 3rem;
}

.create-card,
.digital-io-card {
    border: 1px solid var(--bs-border-color);
    border-radius: 0.85rem;
    overflow: hidden;
}

.create-card {
    background: var(--bs-tertiary-bg);
}

.digital-io-card .card-header {
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid var(--bs-border-color);
}

.digital-io-table thead th {
    padding: 0.85rem 1rem;
    background: var(--bs-tertiary-bg);
    border-bottom: 1px solid var(--bs-border-color);
    color: var(--bs-secondary-color);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
}

.digital-io-table tbody td {
    padding: 1rem;
    vertical-align: middle;
}

.io-type {
    display: inline-flex;
    align-items: center;
    padding: 0.3rem 0.65rem;
    border-radius: 2rem;
    font-size: 0.75rem;
    font-weight: 600;
}

.io-type-input {
    color: var(--bs-primary);
    background: var(--bs-primary-bg-subtle);
}

.io-type-output {
    color: var(--bs-warning-text-emphasis);
    background: var(--bs-warning-bg-subtle);
}

.io-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.5rem;
    border-radius: 0.45rem;
    background: var(--bs-secondary-bg);
    font-weight: 600;
    font-size: 0.85rem;
}

.io-index-input {
    min-width: 5rem;
    max-width: 6rem;
}

.state-date {
    font-size: 0.7rem;
    white-space: nowrap;
}

.empty-state {
    padding: 4rem 1.5rem;
    text-align: center;
}

.empty-state-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 50%;
    background: var(--bs-secondary-bg);
    color: var(--bs-secondary-color);
    font-size: 0.9rem;
    font-weight: 700;
}

.copy-config-card {
    border: 1px solid var(--bs-border-color);
    border-radius: 0.85rem;
    overflow: hidden;
}

.copy-config-card .card-header {
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid var(--bs-border-color);
}

.copy-empty-state {
    padding: 2rem 0.5rem;
    color: var(--bs-secondary-color);
    font-size: 0.875rem;
    text-align: center;
}

.source-io-list {
    border: 1px solid var(--bs-border-color);
    border-radius: 0.65rem;
    overflow: hidden;
}

.source-io-item {
    padding: 0.85rem;
    background: var(--bs-body-bg);
}

.source-io-item + .source-io-item {
    border-top: 1px solid var(--bs-border-color);
}

.source-io-labels {
    display: grid;
    gap: 0.2rem;
    margin-top: 0.65rem;
    font-size: 0.8rem;
}

.copy-help {
    font-size: 0.75rem;
    line-height: 1.4;
}

@media (max-width: 767.98px) {

    .digital-io-page {
        padding-top: 1rem;
    }

    .digital-io-table tbody td,
    .digital-io-table thead th {
        padding-left: 0.75rem;
        padding-right: 0.75rem;
    }

}
</style>